import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import DeleteOutlinedIcon from '@evaneos/b2b-ui/icons/DeleteOutlined';
import AccessTimeIcon from '@evaneos/b2b-ui/icons/AccessTime';
import {
    Box,
    Button,
    paletteColors,
    Stack,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@evaneos/b2b-ui';

import { deleteScheduledMessageRequest } from '../../../../resources/discussion';
import { ScheduledMessage as ScheduledMessageType } from '../../../../resources/discussion/types';
import { DossierId } from '../../../../resources/dossier/types';
import { useAppDispatch } from '../../../../shared/redux';
import { useIsTouchDevice } from '../../../../shared/hooks/useIsTouchDevice';
import { useLongPressMessage } from '../hooks/useLongPressMessage';
import { useActionButtonsCouldBeInside } from '../hooks/useActionButtonsCouldBeInside';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { useGetAgentByDossierId } from '../../../../resources/dossier/queries';
import { messageHtmlConverter } from '../utils/messageHtmlConverter';

import s from './Discussion.css';
import { MessageAttachment } from './MessageAttachment';
import { EditMessageButton } from './EditMessageButton';

const MESSAGE_ACTIONS_CLASS = 'messageActions';

type Props = {
    scheduledMessage: ScheduledMessageType;
    onEdit: () => void;
    isBlurred: boolean;
    isEditing: boolean;
};

export const ScheduledMessage = ({
    scheduledMessage,
    onEdit,
    isBlurred,
    isEditing,
}: Props): JSX.Element | null => {
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { formatMessage, formatDate } = useIntl();
    const isTouchDevice = useIsTouchDevice();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const agent = useGetAgentByDossierId(dossierId);
    const { id, scheduledDate, content, contentHtml, attachments } = scheduledMessage;

    const messageRef = useRef<HTMLDivElement>(null);
    const areActionButtonsInside = useActionButtonsCouldBeInside({
        messageRef,
        minWidthForInsideButton: 250,
    });

    const isEditable = !isBlurred && !isEditing;

    const { isLongPressed, longPressEvents, setIsLongPressed } = useLongPressMessage({
        delay: 300,
        excludeTargets: [messageRef, MESSAGE_ACTIONS_CLASS],
        enabled: isTouchDevice && isEditable,
    });

    if (!agent) {
        return null;
    }

    const onClickEditButton = () => {
        setIsLongPressed(false);
        trackEvent((ampli, defaultProperties) => {
            ampli.scheduledMessageEdited({
                ...defaultProperties,
                ...trackedTripProperties,
                scheduled_datetime: scheduledDate.toISOString(),
            });
        });
        onEdit();
    };

    const onClickDelete = () => {
        setIsLongPressed(false);
        trackEvent((ampli, defaultProperties) => {
            ampli.scheduledMessageDeleted({
                ...defaultProperties,
                ...trackedTripProperties,
                scheduled_datetime: scheduledMessage.scheduledDate.toISOString(),
            });
        });
        dispatch(deleteScheduledMessageRequest(dossierId, id as string));
    };

    return (
        <Stack
            className={s.agentMessage}
            data-testid={`message-id-${id}`}
            sx={{
                ...(!isTouchDevice && {
                    [`&:hover .${MESSAGE_ACTIONS_CLASS}`]: { opacity: 1 },
                }),
                filter: isBlurred ? 'blur(1.5px)' : 'none',
                opacity: isBlurred ? 0.6 : 1,
                pointerEvents: isBlurred ? 'none' : 'auto',
                transition: 'filter 0.4s ease-in-out, opacity 0.4s ease-in-out',
                willChange: 'filter, opacity',
                contain: 'paint',
            }}
            marginTop={{ xs: 2, md: 3 }}
        >
            <Stack flexDirection="column" alignItems="flex-end">
                <Box display="flex" alignItems="center">
                    {isEditable && !areActionButtonsInside && (
                        <Box
                            className={MESSAGE_ACTIONS_CLASS}
                            sx={{
                                display: 'flex',
                                wrap: 'no-wrap',
                                position: isMobile ? 'absolute' : 'sticky',
                                left: '16px',
                                zIndex: 1,
                                transition: 'opacity 0.3s',
                                opacity: isLongPressed ? 1 : 0,
                                ...(isTouchDevice && {
                                    visibility: isLongPressed ? 'visible' : 'hidden',
                                }),
                            }}
                        >
                            <EditMessageButton onClick={onClickEditButton} />
                            <DeleteScheduledMessageButton onClick={onClickDelete} />
                        </Box>
                    )}
                    <Box
                        ref={messageRef}
                        bgcolor={
                            isLongPressed ? paletteColors.green[200] : paletteColors.green[100]
                        }
                        sx={{
                            position: 'relative',
                            maxWidth: '720px',
                            borderRadius: '16px',
                            wordBreak: 'break-word',
                            width: 'fit-content',
                            transition: 'background-color 0.2s ease-in-out',
                            border: '1px dashed',
                            borderColor: 'primary.main',
                        }}
                    >
                        <Box p={2} {...(isTouchDevice && longPressEvents)}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    '& *': {
                                        color: 'text.secondary',
                                        fontSize: '1.4rem',
                                        lineHeight: '2rem',
                                        fontFamily: 'Open Sauce One, sans-serif',
                                    },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: messageHtmlConverter(content, contentHtml),
                                }}
                                data-testid="message-body"
                            />
                            <Box display="flex" flexWrap="wrap">
                                {attachments?.map((attachment) => (
                                    <MessageAttachment key={attachment.id} {...attachment} />
                                ))}
                            </Box>
                        </Box>
                        {isEditable && areActionButtonsInside && (
                            <Box
                                className={MESSAGE_ACTIONS_CLASS}
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    justifyContent: 'flex-end',
                                    transition: 'opacity 0.3s',
                                    opacity: isLongPressed ? 1 : 0,
                                    ...(isTouchDevice && {
                                        visibility: isLongPressed ? 'visible' : 'hidden',
                                    }),
                                }}
                            >
                                <EditMessageButton onClick={onClickEditButton} />
                                <DeleteScheduledMessageButton onClick={onClickDelete} />
                            </Box>
                        )}
                    </Box>
                </Box>
                <Stack flexDirection="row" marginY={0.5} gap={0.5}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        {formatMessage(
                            { id: 'discussion.schedule_messages.preview.schedule_date' },
                            {
                                scheduleDate: formatDate(scheduledDate, {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                }),
                            },
                        )}
                    </Typography>
                    <AccessTimeIcon fontSize="small" htmlColor={paletteColors.neutral[700]} />
                </Stack>
            </Stack>
        </Stack>
    );
};

type DeleteScheduledMessageButtonProps = {
    onClick: () => void;
};

const DeleteScheduledMessageButton = ({
    onClick,
}: DeleteScheduledMessageButtonProps): JSX.Element => {
    const { formatMessage } = useIntl();

    return (
        <Tooltip
            title={formatMessage({ id: 'global.delete' })}
            placement="top"
            slotProps={{
                popper: {
                    // Effectively 4px distance with source element
                    modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
                },
            }}
        >
            <Button
                onClick={onClick}
                color="error"
                sx={{
                    width: '40px',
                    minWidth: '40px',
                    m: 1,
                    ml: 0,
                }}
            >
                <DeleteOutlinedIcon />
            </Button>
        </Tooltip>
    );
};
